<template>
  <div class="fortune-spin-wrapper">
    <!-- Десктопная заглушка -->
    <div class="desktop-stub">
      <div class="desktop-stub-content">
        <div class="desktop-stub-top">
          <div class="lang-switch-container">
            <button 
              type="button" 
              class="lang-btn" 
              :class="{ active: currentLang === 'ru' }" 
              @click="setLanguage('ru')"
            >
              RU
            </button>
            <span class="lang-divider">|</span>
            <button 
              type="button" 
              class="lang-btn" 
              :class="{ active: currentLang === 'kz' }" 
              @click="setLanguage('kz')"
            >
              KZ
            </button>
          </div>
        </div>
        <div class="desktop-stub-icon">📱</div>
        <h2 class="desktop-stub-title">{{ $t('desktopStub.title') }}</h2>
        <p class="desktop-stub-subtitle">
          {{ $t('desktopStub.subtitle') }}
        </p>
      </div>
    </div>

    <!-- Мобильная версия с 4 экранами (степпер) -->
    <div class="mobile-container">
      <!-- Индикатор степпера -->
      <div class="stepper-bar">
        <div 
          v-for="step in 4" 
          :key="step" 
          class="stepper-dot"
          :class="{ active: currentStep === step, completed: currentStep > step }"
        ></div>
      </div>

      <!-- ЭКРАН 1 -->
      <div v-if="currentStep === 1" class="screen screen-1">
        <!-- Header (красный фон) -->
        <header class="screen-header">
          <span class="header-left">{{ $t('screen1.headerLeft') }}</span>
          <div class="header-right-group">
            <button 
              type="button" 
              class="btn-promoter-reset" 
              :title="$t('common.newParticipant')" 
              @click="startNewParticipant"
            >
              <span class="promoter-reset-icon">↺</span>
              <span class="promoter-reset-text">{{ $t('common.newParticipant') }}</span>
            </button>
            <div class="lang-switch-container">
              <button 
                type="button" 
                class="lang-btn" 
                :class="{ active: currentLang === 'ru' }" 
                @click="setLanguage('ru')"
              >
                RU
              </button>
              <span class="lang-divider">|</span>
              <button 
                type="button" 
                class="lang-btn" 
                :class="{ active: currentLang === 'kz' }" 
                @click="setLanguage('kz')"
              >
                KZ
              </button>
            </div>
            <span class="header-age-badge">{{ $t('common.ageConfirmed') }}</span>
          </div>
        </header>

        <!-- Главное изображение -->
        <div class="main-image-wrapper">
          <img src="@/assets/img/fortune-main-photo.png" alt="Absolut Tabasco" class="main-image" />
        </div>

        <!-- Нижний блок (красный фон) -->
        <div class="bottom-block">
          <div class="sub-header-text">{{ $t('screen1.subHeader') }}</div>

          <h1 class="main-title">
            <span class="title-line">{{ $t('screen1.titleLine1') }}</span>
            <span class="title-line">{{ $t('screen1.titleLine2') }}</span>
          </h1>

          <p class="instruction-text">
            {{ $t('screen1.instruction') }}
          </p>

          <button class="btn-participate" @click="nextStep">
            {{ $t('screen1.participateBtn') }}
          </button>

          <p class="disclaimer-text">
            {{ $t('screen1.disclaimer') }}
          </p>
        </div>
      </div>

      <!-- ЭКРАН 2 (Форма) -->
      <div v-else-if="currentStep === 2" class="screen screen-2">
        <!-- Шапка (Header) -->
        <header class="screen-header step-header">
          <span class="header-left">{{ $t('screen2.stepHeaderLeft') }}</span>
          <div class="header-right-group">
            <button 
              type="button" 
              class="btn-promoter-reset" 
              :title="$t('common.newParticipant')" 
              @click="startNewParticipant"
            >
              <span class="promoter-reset-icon">↺</span>
              <span class="promoter-reset-text">{{ $t('common.newParticipant') }}</span>
            </button>
            <div class="lang-switch-container">
              <button 
                type="button" 
                class="lang-btn" 
                :class="{ active: currentLang === 'ru' }" 
                @click="setLanguage('ru')"
              >
                RU
              </button>
              <span class="lang-divider">|</span>
              <button 
                type="button" 
                class="lang-btn" 
                :class="{ active: currentLang === 'kz' }" 
                @click="setLanguage('kz')"
              >
                KZ
              </button>
            </div>
            <span class="header-brand-text">{{ $t('screen2.stepHeaderRight') }}</span>
          </div>
        </header>

        <div class="screen-2-body">
          <!-- Заголовок -->
          <div class="screen-title-block">
            <div class="subtitle-text">{{ $t('screen2.subtitle') }}</div>
            <h1 class="heading-title">{{ $t('screen2.heading') }}</h1>
          </div>

          <!-- Состояние загрузки формы -->
          <div v-if="isLoadingForm" class="form-loading">
            <v-progress-circular indeterminate size="48" color="#FFFFFF" />
          </div>

          <!-- Состояние ошибки загрузки формы -->
          <div v-else-if="formError" class="form-error">
            <p class="form-error-text">{{ $t('screen2.formError') }}</p>
            <button type="button" class="btn-participate btn-retry" @click="getFormFields">
              {{ $t('common.retry') }}
            </button>
          </div>

          <!-- Основная форма -->
          <div v-else class="registration-form">
            <!-- Динамический рендеринг полей опроса, если схема получена -->
            <template v-if="formSchema && formSchema.questions && formSchema.questions.length > 0">
              <div v-for="question in normalQuestions" :key="question.id" class="form-group">
                <label class="form-label">{{ getQuestionName(question) }}</label>
                
                <!-- Текстовые поля (Имя, Фамилия, Email, Текст) -->
                <div v-if="[1, 2, 7, 10].includes(question.type)">
                  <input 
                    class="form-input"
                    v-model="formData[question.id]" 
                    :placeholder="getPlaceholder(question)"
                    @blur="markTouched(question)" 
                    @input="validateField(question)" 
                  />
                  <div class="error-message">
                    <span v-show="errors[question.id]">{{ errors[question.id] }}</span>
                  </div>
                </div>

                <!-- Телефон (Казахстанский формат) -->
                <div v-else-if="question.type === 4">
                  <div class="phone-input-wrapper">
                    <input 
                      class="phone-input"
                      v-model="formData[question.id]" 
                      v-mask="'+7(###) ###-##-##'"
                      :placeholder="getPlaceholder(question)" 
                      @blur="markTouched(question)"
                      @focus="onPhoneFocus(question)" 
                      @input="onPhoneInput(question)" 
                    />
                  </div>
                  <div class="error-message">
                    <span v-show="errors[question.id]">{{ errors[question.id] }}</span>
                  </div>
                </div>

                <!-- Пол (type === 6) - Переключатель кнопками (как в 1 форме) -->
                <div v-else-if="question.type === 6">
                  <div class="gender-toggle-group">
                    <template v-if="question.answers && question.answers.length > 0">
                      <button 
                        v-for="ans in question.answers" 
                        :key="ans.id"
                        type="button" 
                        class="gender-btn" 
                        :class="formData[question.id] === ans.id ? 'gender-btn-white' : 'gender-btn-red'"
                        @click="selectGender(question, ans.id)"
                      >
                        {{ (currentLang === 'kz' && ans.name_Kaz) || ans.name }}
                      </button>
                    </template>
                    <template v-else>
                      <button 
                        type="button" 
                        class="gender-btn" 
                        :class="formData[question.id] === 'male' ? 'gender-btn-white' : 'gender-btn-red'"
                        @click="selectGender(question, 'male')"
                      >
                        {{ $t('screen2.gender.male') }}
                      </button>
                      <button 
                        type="button" 
                        class="gender-btn" 
                        :class="formData[question.id] === 'female' ? 'gender-btn-white' : 'gender-btn-red'"
                        @click="selectGender(question, 'female')"
                      >
                        {{ $t('screen2.gender.female') }}
                      </button>
                    </template>
                  </div>
                  <div class="error-message">
                    <span v-show="errors[question.id]">{{ errors[question.id] }}</span>
                  </div>
                </div>

                <!-- Выпадающие списки (Город, Выбор продукта) -->
                <div v-else-if="[5, 9].includes(question.type)">
                  <v-select 
                    v-model="formData[question.id]"
                    :items="question.answers" 
                    :item-text="currentLang === 'kz' ? 'name_Kaz' : 'name'" 
                    item-value="id" 
                    dense 
                    outlined
                    hide-details 
                    :placeholder="getPlaceholder(question)" 
                    class="fortune-select"
                  >
                    <template v-slot:selection="{ item }">
                      <div class="select-item-text">
                        {{ (currentLang === 'kz' && item.name_Kaz) || item.name }}
                      </div>
                    </template>
                  </v-select>
                  <div class="error-message">
                    <span v-show="errors[question.id]">{{ errors[question.id] }}</span>
                  </div>
                </div>
              </div>

              <!-- Чекбоксы согласий (текст приходит из запроса) -->
              <div class="consent-group mt-2">
                <div v-for="question in filteredQuestions" :key="question.id" class="custom-checkbox-wrapper">
                  <label class="custom-checkbox-label" :for="'spin_' + question.id">
                    <input 
                      class="checkbox-input" 
                      type="checkbox" 
                      v-model="formData[question.id]"
                      :id="'spin_' + question.id" 
                      @change="markTouched(question)"
                      @input="validateField(question)" 
                    />
                    <span class="custom-checkbox"></span>
                    <span class="checkbox-text" @click="handleCheckboxLinkClick($event)">
                      <span v-html="formatCheckboxText(question)"></span>
                    </span>
                  </label>
                  <div class="error-message">
                    <span v-show="errors[question.id]">{{ errors[question.id] }}</span>
                  </div>
                </div>
              </div>

              <!-- Подвал формы -->
              <div class="form-footer">
                <p class="form-disclaimer">{{ $t('screen2.disclaimer') }}</p>
                <button 
                  type="button" 
                  class="btn-participate btn-submit-step2" 
                  :disabled="isSendSms"
                  @click="confirmRegistration"
                >
                  {{ isSendSms ? $t('screen2.sendingBtn') : $t('screen2.submitBtn') }}
                </button>
              </div>
            </template>

            <!-- Фоллбек форма, если campaignId еще не указан или схема не загружена -->
            <template v-else>
              <!-- Поле 1 (ФИО) -->
              <div class="form-group">
                <label class="form-label">{{ $t('screen2.placeholders.fullName') }}</label>
                <input 
                  v-model="form.fullName" 
                  type="text" 
                  class="form-input" 
                  :placeholder="$t('screen2.placeholders.fullName')" 
                />
              </div>

              <!-- Поле 2 (Пол) -->
              <div class="form-group">
                <label class="form-label">{{ $t('screen2.gender.label') }}</label>
                <div class="gender-toggle-group">
                  <button 
                    type="button" 
                    class="gender-btn" 
                    :class="form.gender === 'male' ? 'gender-btn-white' : 'gender-btn-red'"
                    @click="form.gender = 'male'"
                  >
                    {{ $t('screen2.gender.male') }}
                  </button>
                  <button 
                    type="button" 
                    class="gender-btn" 
                    :class="form.gender === 'female' ? 'gender-btn-white' : 'gender-btn-red'"
                    @click="form.gender = 'female'"
                  >
                    {{ $t('screen2.gender.female') }}
                  </button>
                </div>
              </div>

              <!-- Поле 3 (Номер телефона) -->
              <div class="form-group">
                <label class="form-label">{{ $t('screen2.phoneLabel') }}</label>
                <div class="phone-input-wrapper">
                  <input 
                    v-model="form.phone" 
                    v-mask="'+7(###) ###-##-##'"
                    type="tel" 
                    class="phone-input" 
                    :placeholder="$t('screen2.placeholders.phone')" 
                  />
                </div>
              </div>

              <!-- Чекбоксы согласий -->
              <div class="consent-group">
                <label class="custom-checkbox-label">
                  <input v-model="form.consentData" type="checkbox" class="checkbox-input" />
                  <span class="custom-checkbox"></span>
                  <span class="checkbox-text">
                    {{ $t('screen2.consent.personalDataPrefix') }}
                    <a class="legal-link" @click.stop.prevent="openPopup('offer')">{{ $t('screen2.consent.personalDataLink') }}</a>.
                  </span>
                </label>

                <label class="custom-checkbox-label">
                  <input v-model="form.consentMarketing" type="checkbox" class="checkbox-input" />
                  <span class="custom-checkbox"></span>
                  <span class="checkbox-text">
                    {{ $t('screen2.consent.marketing') }}
                  </span>
                </label>
              </div>

              <!-- Подвал формы -->
              <div class="form-footer">
                <p class="form-disclaimer">{{ $t('screen2.disclaimer') }}</p>
                <button 
                  type="button" 
                  class="btn-participate btn-submit-step2" 
                  :disabled="isSendSms"
                  @click="confirmRegistration"
                >
                  {{ isSendSms ? $t('screen2.sendingBtn') : $t('screen2.submitBtn') }}
                </button>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- ЭКРАН 3 (Колесо Фортуны) -->
      <div v-else-if="currentStep === 3" class="screen screen-3">
        <!-- Шапка (Header) -->
        <header class="screen-header step-header">
          <span class="header-left">{{ $t('screen3.stepHeaderLeft') }}</span>
          <div class="header-right-group">
            <button 
              type="button" 
              class="btn-promoter-reset" 
              :title="$t('common.newParticipant')" 
              @click="startNewParticipant"
            >
              <span class="promoter-reset-icon">↺</span>
              <span class="promoter-reset-text">{{ $t('common.newParticipant') }}</span>
            </button>
            <div class="lang-switch-container">
              <button 
                type="button" 
                class="lang-btn" 
                :class="{ active: currentLang === 'ru' }" 
                @click="setLanguage('ru')"
              >
                RU
              </button>
              <span class="lang-divider">|</span>
              <button 
                type="button" 
                class="lang-btn" 
                :class="{ active: currentLang === 'kz' }" 
                @click="setLanguage('kz')"
              >
                KZ
              </button>
            </div>
            <span class="header-brand-text">{{ $t('screen3.stepHeaderRight') }}</span>
          </div>
        </header>

        <div class="screen-3-body">
          <!-- Главный заголовок -->
          <h1 class="wheel-main-title">{{ $t('screen3.title') }}</h1>

          <!-- Блок Колеса -->
          <div class="wheel-container">
            <!-- Указатель сверху -->
            <div class="wheel-pointer">
              <svg width="32" height="36" viewBox="0 0 32 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 36L0.411543 0.75L31.5885 0.75L16 36Z" fill="#FFFFFF" />
                <path d="M16 31L4.5 2.5L27.5 2.5L16 31Z" fill="#DD1814" />
              </svg>
            </div>

            <!-- Вращающееся Колесо -->
            <div 
              class="wheel-rotator"
              :style="{ transform: `rotate(${wheelRotation}deg)` }"
            >
              <svg viewBox="0 0 400 400" class="wheel-svg">
                <g v-for="sector in sectorPaths" :key="sector.id">
                  <!-- Сектор колеса -->
                  <path 
                    :d="sector.pathData" 
                    :fill="sector.bgColor" 
                    stroke="#FFFFFF" 
                    stroke-width="2" 
                  />
                  
                  <!-- Содержимое сектора (Текст / Иконка + Подпись) -->
                  <g :transform="`translate(${sector.tx}, ${sector.ty}) rotate(${sector.textRotation})`">
                    <text 
                      v-if="sector.type === 'text'" 
                      x="0" 
                      y="0" 
                      text-anchor="middle" 
                      dominant-baseline="central" 
                      :fill="sector.textColor"
                      class="sector-text"
                    >
                      {{ sector.text }}
                    </text>
                    <g v-else>
                      <text 
                        x="0" 
                        y="-8" 
                        text-anchor="middle" 
                        dominant-baseline="central" 
                        class="sector-emoji"
                      >
                        {{ sector.icon }}
                      </text>
                      <!-- <text 
                        x="0" 
                        y="14" 
                        text-anchor="middle" 
                        dominant-baseline="central" 
                        :fill="sector.textColor"
                        class="sector-label"
                      >
                        {{ sector.text }}
                      </text> -->
                    </g>
                  </g>
                </g>
                <circle cx="200" cy="200" r="195" fill="none" stroke="#FFFFFF" stroke-width="4" />
              </svg>
            </div>

            <!-- Круглый белый бейдж в центре -->
            <div class="wheel-center-badge">
              <span class="badge-text">{{ $t('screen3.badge') }}</span>
            </div>
          </div>

          <!-- Дисклеймер -->
          <p class="wheel-disclaimer">
            {{ $t('screen3.disclaimer') }}
          </p>

          <!-- Кнопка КРУТИТЬ -->
          <button 
            class="btn-participate btn-spin" 
            :disabled="isSpinning" 
            @click="handleSpin"
          >
            {{ isSpinning ? $t('screen3.spinningBtn') : $t('screen3.spinBtn') }}
          </button>
        </div>
      </div>

      <!-- ЭКРАН 4 (Экран результата) -->
      <div v-else-if="currentStep === 4" class="screen screen-4">
        <!-- Шапка (Header) -->
        <header class="screen-header step-header">
          <span class="header-left">{{ $t('screen4.stepHeaderLeft') }}</span>
          <div class="header-right-group">
            <button 
              type="button" 
              class="btn-promoter-reset" 
              :title="$t('common.newParticipant')" 
              @click="startNewParticipant"
            >
              <span class="promoter-reset-icon">↺</span>
              <span class="promoter-reset-text">{{ $t('common.newParticipant') }}</span>
            </button>
            <div class="lang-switch-container">
              <button 
                type="button" 
                class="lang-btn" 
                :class="{ active: currentLang === 'ru' }" 
                @click="setLanguage('ru')"
              >
                RU
              </button>
              <span class="lang-divider">|</span>
              <button 
                type="button" 
                class="lang-btn" 
                :class="{ active: currentLang === 'kz' }" 
                @click="setLanguage('kz')"
              >
                KZ
              </button>
            </div>
            <span class="header-brand-text">{{ $t('screen4.stepHeaderRight') }}</span>
          </div>
        </header>

        <div class="screen-4-body">
          <!-- Заголовок приза -->
          <div class="result-title-block">
            <h1 class="prize-title">{{ prize.title }}</h1>
            <!-- <p class="prize-subtitle">{{ prize.subtitle }}</p> -->
          </div>

          <!-- Инструкция для физического приза (v-if) -->
          <div v-if="prize.isPhysical" class="physical-instruction-card">
            <div class="instruction-icon">{{ prize.icon || '🎪' }}</div>
            <p class="instruction-text-right">
              {{ $t('screen4.physicalInstruction') }}
            </p>
          </div>

          <!-- 3. Карточка с промокодом (Promo Code Card) -->
          <div v-if="prize.promoCode" class="promo-card-wrapper">
            <div class="promo-card">
              <span class="promo-header-text">{{ $t('screen4.promoHeader') }}</span>
              <div class="promo-code-value">{{ prize.promoCode }}</div>
              <p class="promo-instruction-text">{{ $t('screen4.promoInstruction') }}</p>
            </div>
            <!-- Текст под карточкой -->
            <p class="promo-expiry-text">
              {{ $t('screen4.promoExpiryPrefix') }}<br />
              {{ $t('screen4.promoExpiryUntil', { date: prize.expiryDate || $t('screen4.promoExpiryDefaultDate') }) }}
            </p>
          </div>

          <!-- 4. Блок с QR-кодом (QR Code Card) -->
          <div class="qr-block-wrapper">
            <div class="qr-card">
              <img src="../assets/img/fortune-qr.png" class="qr-code-svg">
              <!-- Схематичная иконка QR-кода -->
            </div>
            <!-- Текст под QR кодом -->
            <p class="qr-caption-text">
              {{ $t('screen4.qrCaption') }}
            </p>
          </div>

          <!-- Кнопка перехода -->
          <a 
            :href="partnerLink" 
            target="_blank" 
            rel="noopener noreferrer" 
            class="btn-participate btn-partner-link"
          >
            {{ $t('screen4.partnerBtn') }}
          </a>

          <!-- Кнопка для промоутера: Следующий участник -->
          <button 
            type="button" 
            class="btn-participate btn-next-participant" 
            @click="startNewParticipant"
          >
            {{ $t('screen4.nextParticipantBtn') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Компонент AgeGate (Подтверждение возраста 21+) -->
    <ConfirmAge1 v-if="showConfirm" @confirmed="onConfirmed" />

    <!-- Компонент OTP верификации номера телефона -->
    <otp 
      :key="otpKey"
      :showPopup="showOtpPopup" 
      :smsTimer="smsTimer" 
      :errorSms="otpErrorSms" 
      @resend="resendSms" 
      @verifyCode="submitAnswersWithCode"
      @closePopup="onCloseOtpPopup" 
      :success="otpSuccess" 
      :errorMessage="otpError"
      :loading="otpVerifying" 
    />

    <!-- Попап с юридической информацией (Правила, Оферта, Политика) -->
    <transition name="popup">
      <div v-if="isPopupOpen" class="popup-overlay" @click.self="closePopup">
        <div class="popup-content">
          <div class="popup-header">
            <span class="header-text">{{ currentPopupData.title }}</span>
            <span class="popup-close-1" @click="closePopup">
              <svg width="21" height="21" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M1.07373 33.4784C-0.348236 34.8712 -0.377256 37.4247 1.10275 38.9046C2.58275 40.3845 5.13648 40.3264 6.52942 38.9336L19.9945 25.4697L33.4597 38.9046C34.9106 40.3555 37.4063 40.3845 38.8863 38.8756C40.3663 37.3957 40.3663 34.9003 38.9154 33.4494L25.4793 20.0145L38.9154 6.5506C40.3663 5.09975 40.3663 2.60428 38.8863 1.12441C37.4063 -0.384476 34.9106 -0.355459 33.4597 1.09539L19.9945 14.5303L6.52942 1.06638C5.13648 -0.326442 2.58275 -0.384476 1.10275 1.09539C-0.377256 2.57526 -0.348236 5.12876 1.07373 6.5506L14.5388 20.0145L1.07373 33.4784Z"
                  fill="white"
                />
              </svg>
            </span>
          </div>
          <div class="popup-text-1" v-html="currentPopupData.content"></div>
        </div>
      </div>
    </transition>

    <!-- Снэкбар для системных уведомлений и сообщений валидации -->
    <v-snackbar v-model="snackbar" timeout="3000" class="fortune-snackbar">
      {{ message }}
      <template v-slot:action="{ attrs }">
        <div class="snackbar-btn" text v-bind="attrs" @click="snackbar = false">
          {{ $t('common.close') }}
        </div>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mask } from "vue-the-mask";
import { mapGetters, mapActions } from "vuex";
import Otp from "@/components/otp.vue";
import ConfirmAge1 from "@/components/age-confirm.vue";

export default {
  name: "FortuneSpin",
  directives: { mask },
  components: {
    Otp,
    ConfirmAge1
  },
  data() {
    return {
      // Идентификатор кампании и колеса фортуны
      campaignId: "118aac9d-a897-f111-b801-00155d79212a",
      wheelId: "1BAE3EFB-2696-F111-B801-00155D79212A",
      answerSetId: null,

      // Управление экранами и состоянием игры
      currentStep: 1,
      isSpinning: false,
      wheelRotation: 0,
      winningSector: null,
      copied: false,
      lastSpinResult: null,
      lastWinningIndex: null,

      // Управление AgeGate
      showConfirm: true,

      // Управление формой и опросом
      isLoadingForm: false,
      formError: false,
      formSchema: null,
      formData: {},
      errors: {},
      touched: {},

      // Локальные данные формы (фоллбек, пока campaignId не указан)
      form: {
        fullName: "",
        gender: "male",
        phone: "",
        consentData: false,
        consentMarketing: false
      },

      // Управление OTP верификацией
      otpKey: 0,
      showOtpPopup: false,
      isSendSms: false,
      smsTimer: 60,
      smsTimerInterval: null,
      otpSuccess: false,
      otpVerifying: false,
      otpError: "",
      otpErrorSms: false,

      // Управление уведомлениями и попапами
      snackbar: false,
      message: "",
      isPopupOpen: false,
      currentPopup: null,

      // Текущий приз
      prize: {
        id: null,
        type: "digital",
        isPhysical: false,
        title: "",
        subtitle: "",
        icon: null,
        promoCode: "",
        expiryDate: "",
        partnerBaseUrl: "https://newxo.kz/product/vodka-absolut-tabasco-07-l"
      }
    };
  },
  created() {
    const rawAge = localStorage.getItem("userAge");
    if (!rawAge || rawAge === "null" || rawAge === "undefined") {
      this.showConfirm = true;
      localStorage.removeItem("userAge");
    } else {
      this.showConfirm = false;
    }
    if (this.$route?.query?.answerSetId) {
      this.answerSetId = this.$route.query.answerSetId;
    }
    if (this.$route?.query?.wheelId) {
      this.wheelId = this.$route.query.wheelId;
    }
  },
  computed: {
    ...mapGetters(["getUserAge"]),
    currentLang() {
      return this.$i18n.locale;
    },
    currentPopupData() {
      if (!this.currentPopup) return { title: "", content: "" };
      return {
        title: this.$t(`legal.${this.currentPopup}.title`),
        content: this.$t(`legal.${this.currentPopup}.content`)
      };
    },
    sectors() {
      return [
        { id: 0, text: "-20%", type: "text", bgColor: "#FFFFFF", textColor: "#DD1814", giftId: "d0410ec2-2896-f111-b801-00155d79212a" },
        { id: 1, icon: "🎁", text: this.$t("prizes.fan.sectorText"), type: "icon", bgColor: "#A90C01", textColor: "#FFFFFF", giftId: "64a02526-2996-f111-b801-00155d79212a" },
        { id: 2, text: "-20%", type: "text", bgColor: "#FFFFFF", textColor: "#DD1814", giftId: "d0410ec2-2896-f111-b801-00155d79212a" },
        { id: 3, icon: "🎁", text: this.$t("prizes.visor.sectorText"), type: "icon", bgColor: "#A90C01", textColor: "#FFFFFF", giftId: "ce380775-299a-f111-b802-00155d79212a" },
        { id: 4, text: "-20%", type: "text", bgColor: "#FFFFFF", textColor: "#DD1814", giftId: "d0410ec2-2896-f111-b801-00155d79212a" },
        { id: 5, icon: "🎁", text: this.$t("prizes.blanket.sectorText"), type: "icon", bgColor: "#A90C01", textColor: "#FFFFFF", giftId: "544e0da8-2996-f111-b801-00155d79212a" },
        { id: 6, text: "-20%", type: "text", bgColor: "#FFFFFF", textColor: "#DD1814", giftId: "d0410ec2-2896-f111-b801-00155d79212a" },
        { id: 7, icon: "🎁", text: this.$t("prizes.bag.sectorText"), type: "icon", bgColor: "#A90C01", textColor: "#FFFFFF", giftId: "c25f6d47-299a-f111-b802-00155d79212a" }
      ];
    },
    giftsCatalog() {
      return {
        "64a02526-2996-f111-b801-00155d79212a": {
          id: "64a02526-2996-f111-b801-00155d79212a",
          name: this.$t("prizes.fan.name"),
          title: this.$t("prizes.fan.title"),
          subtitle: this.$t("prizes.fan.subtitle"),
          icon: "🎁",
          sectorIndex: 1
        },
        "ce380775-299a-f111-b802-00155d79212a": {
          id: "ce380775-299a-f111-b802-00155d79212a",
          name: this.$t("prizes.visor.name"),
          title: this.$t("prizes.visor.title"),
          subtitle: this.$t("prizes.visor.subtitle"),
          icon: "🎁",
          sectorIndex: 3
        },
        "544e0da8-2996-f111-b801-00155d79212a": {
          id: "544e0da8-2996-f111-b801-00155d79212a",
          name: this.$t("prizes.blanket.name"),
          title: this.$t("prizes.blanket.title"),
          subtitle: this.$t("prizes.blanket.subtitle"),
          icon: "🎁",
          sectorIndex: 5
        },
        "c25f6d47-299a-f111-b802-00155d79212a": {
          id: "c25f6d47-299a-f111-b802-00155d79212a",
          name: this.$t("prizes.bag.name"),
          title: this.$t("prizes.bag.title"),
          subtitle: this.$t("prizes.bag.subtitle"),
          icon: "🎁",
          sectorIndex: 7
        },
        "d0410ec2-2896-f111-b801-00155d79212a": {
          id: "d0410ec2-2896-f111-b801-00155d79212a",
          name: this.$t("prizes.promocode.name"),
          title: this.$t("prizes.promocode.title"),
          subtitle: this.$t("prizes.promocode.subtitle"),
          icon: null,
          sectorIndices: [0, 2, 4, 6]
        }
      };
    },
    filteredQuestions() {
      if (!this.formSchema?.questions) return [];
      return this.formSchema.questions.filter(q => q.type === 11 || q.type === 12);
    },
    normalQuestions() {
      if (!this.formSchema?.questions) return [];
      return this.formSchema.questions.filter(q => q.type !== 11 && q.type !== 3 && q.type !== 12);
    },
    sectorPaths() {
      const cx = 200;
      const cy = 200;
      const r = 195;
      const textR = 130;
      const total = this.sectors.length;
      const angleStep = 360 / total;

      return this.sectors.map((sector, i) => {
        const startDeg = i * angleStep - angleStep / 2 - 90;
        const endDeg = i * angleStep + angleStep / 2 - 90;
        const midDeg = i * angleStep - 90;

        const startRad = (startDeg * Math.PI) / 180;
        const endRad = (endDeg * Math.PI) / 180;
        const midRad = (midDeg * Math.PI) / 180;

        const x1 = cx + r * Math.cos(startRad);
        const y1 = cy + r * Math.sin(startRad);
        const x2 = cx + r * Math.cos(endRad);
        const y2 = cy + r * Math.sin(endRad);

        const tx = cx + textR * Math.cos(midRad);
        const ty = cy + textR * Math.sin(midRad);

        const pathData = `M ${cx} ${cy} L ${x1.toFixed(2)} ${y1.toFixed(2)} A ${r} ${r} 0 0 1 ${x2.toFixed(2)} ${y2.toFixed(2)} Z`;

        return {
          ...sector,
          pathData,
          tx: tx.toFixed(2),
          ty: ty.toFixed(2),
          textRotation: i * angleStep
        };
      });
    },
    partnerLink() {
      if (!this.prize) return "https://newxo.kz/product/vodka-absolut-tabasco-07-l";
      const baseUrl = this.prize.partnerBaseUrl || "https://newxo.kz/product/vodka-absolut-tabasco-07-l";
      const code = this.prize.promoCode || "";
      return code ? `${baseUrl}?promo=${encodeURIComponent(code)}` : baseUrl;
    }
  },
  watch: {
    formData: {
      deep: true,
      handler(val) {
        for (let [id, value] of Object.entries(val)) {
          this.validateField(id, value);
        }
      }
    },
    getUserAge(newVal) {
      if (!newVal || !this.formSchema?.questions) return;
      const birthQuestion = this.formSchema.questions.find(q => q.type === 3);
      if (birthQuestion) {
        this.$set(this.formData, birthQuestion.id, newVal);
      }
    }
  },
  mounted() {
    if (this.campaignId) {
      this.getFormFields();
    }
  },
  beforeDestroy() {
    if (this.smsTimerInterval) {
      clearInterval(this.smsTimerInterval);
    }
  },
  methods: {
    ...mapActions(["saveUserAge", "clearUserAge"]),

    startNewParticipant() {
      // 1. Очищаем возраст участника из Vuex и localStorage
      this.clearUserAge();
      localStorage.removeItem("userAge");

      // 2. Сбрасываем форму опроса
      this.formData = {};
      this.form = {
        fullName: "",
        gender: "male",
        phone: "",
        consentData: false,
        consentMarketing: false
      };
      this.errors = {};
      this.touched = {};
      this.answerSetId = null;

      // 3. Сбрасываем OTP
      this.showOtpPopup = false;
      this.isSendSms = false;
      this.otpSuccess = false;
      this.otpVerifying = false;
      this.otpError = "";
      this.otpErrorSms = false;
      this.otpKey++;
      if (this.smsTimerInterval) {
        clearInterval(this.smsTimerInterval);
        this.smsTimerInterval = null;
      }
      this.smsTimer = 60;

      // 4. Сбрасываем Колесо Фортуны и призы
      this.isSpinning = false;
      this.winningSector = null;
      this.lastSpinResult = null;
      this.lastWinningIndex = null;
      this.wheelRotation = 0;
      this.copied = false;
      this.prize = {
        id: null,
        type: "digital",
        isPhysical: false,
        title: "",
        subtitle: "",
        icon: null,
        promoCode: "",
        expiryDate: "",
        partnerBaseUrl: "https://newxo.kz/product/vodka-absolut-tabasco-07-l"
      };

      // 5. Возвращаем степпер на 1 экран и открываем AgeGate для нового участника
      this.currentStep = 1;
      this.showConfirm = true;

      // 6. Перезагружаем/очищаем поля формы
      this.getFormFields();

      // 7. Скроллим страницу вверх
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    setLanguage(lang) {
      this.$i18n.locale = lang;
      localStorage.setItem("userLang", lang);
      if (this.lastSpinResult) {
        this.applySpinResult(this.lastSpinResult, false);
      }
      if (this.formData) {
        Object.keys(this.touched).forEach(key => {
          if (this.touched[key]) {
            this.validateField(key);
          }
        });
      }
    },
    getQuestionName(question) {
      if (!question) return "";
      if (this.currentLang === "kz" && question.name_Kaz) {
        return question.name_Kaz;
      }
      return question.name || "";
    },
    onConfirmed() {
      this.showConfirm = false;
      if (this.formSchema?.questions) {
        const birthQuestion = this.formSchema.questions.find(q => q.type === 3);
        if (birthQuestion && this.getUserAge) {
          this.$set(this.formData, birthQuestion.id, this.getUserAge);
        }
      }
      console.log("Возраст подтвержден через AgeGate:", this.getUserAge);
    },
    nextStep() {
      if (this.currentStep < 4) {
        this.currentStep++;
      }
    },
    prevStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
      }
    },
    goToStep(step) {
      if (step >= 1 && step <= 4) {
        this.currentStep = step;
      }
    },
    openPopup(type) {
      this.currentPopup = type;
      this.isPopupOpen = true;
    },
    closePopup() {
      this.isPopupOpen = false;
    },
    onCloseOtpPopup() {
      this.showOtpPopup = false;
      this.otpErrorSms = false;
      if (this.otpSuccess) {
        this.goToStep(3);
      }
    },
    selectGender(question, val) {
      this.$set(this.formData, question.id, val);
      this.markTouched(question);
      this.validateField(question);
    },
    formatCheckboxText(question) {
      const text = this.getQuestionName(question);
      if (!text) {
        if (question.type === 11 || question.number === 7) {
          return `${this.$t("screen2.consent.personalDataPrefix")} <a class="legal-link" data-popup="offer">${this.$t("screen2.consent.personalDataLink")}</a>`;
        }
        if (question.type === 12) {
          return `${this.$t("screen2.consent.rulesPrefix")} <a class="legal-link" data-popup="rules">${this.$t("screen2.consent.rulesLink")}</a>`;
        }
        return "";
      }

      let formatted = text;

      // Оборачиваем в кликабельные ссылки юридические формулировки
      formatted = formatted.replace(
        /(сбор и обработку персональных данных|сбору и обработке персональных данных|дербес деректерді жинауға және өңдеуге|дербес деректерді жинау мен өңдеуге)/gi,
        '<a class="legal-link" data-popup="offer">$1</a>'
      );

      formatted = formatted.replace(
        /(правилами участия|правила участия|условиями проведения|условиями акции|қатысу ережелерімен|қатысу шарттарымен)/gi,
        '<a class="legal-link" data-popup="rules">$1</a>'
      );

      formatted = formatted.replace(
        /(политикой конфиденциальности|политика конфиденциальности|құпиялылық саясаты)/gi,
        '<a class="legal-link" data-popup="policy">$1</a>'
      );

      return formatted;
    },
    handleCheckboxLinkClick(e) {
      const target = e.target.closest("a[data-popup]");
      if (target) {
        e.preventDefault();
        e.stopPropagation();
        const popupType = target.getAttribute("data-popup");
        if (popupType) {
          this.openPopup(popupType);
        }
      }
    },
    getPlaceholder(question) {
      const isKz = this.currentLang === "kz";
      switch (question.type) {
        case 1:
          return (isKz && question.name_Kaz) || question.name || this.$t("screen2.placeholders.name");
        case 2:
          return (isKz && question.name_Kaz) || question.name || this.$t("screen2.placeholders.surname");
        case 4:
          return this.$t("screen2.placeholders.phone");
        case 5:
          return (isKz && question.name_Kaz) || question.name || this.$t("screen2.placeholders.city");
        case 6:
          return (isKz && question.name_Kaz) || question.name || this.$t("screen2.placeholders.gender");
        case 7:
          return (isKz && question.name_Kaz) || question.name || this.$t("screen2.placeholders.email");
        case 9:
          return (isKz && question.name_Kaz) || question.name || this.$t("screen2.placeholders.select");
        case 10:
          return (isKz && question.name_Kaz) || question.name || this.$t("screen2.placeholders.login");
        default:
          return (isKz && question.name_Kaz) || question.name;
      }
    },
    validateField(question) {
      let q = question;
      if (typeof question === "string" || typeof question === "number") {
        q = this.formSchema?.questions?.find(item => String(item.id) === String(question));
      }
      if (!q) return;

      const value = this.formData[q.id];
      let error = null;

      if ([1, 2, 4, 7].includes(q.type)) {
        if (!value || String(value).trim() === "") {
          error = this.$t("screen2.errors.required");
        }
      }
      if (q.type === 11 || q.type === 12) {
        if (!value && this.touched[q.id]) {
          error = this.$t("screen2.errors.consentRequired");
        }
      }
      if (error === null && value) {
        if (q.type === 1 || q.type === 2) {
          if (!/^[А-Яа-яA-Za-zӘәҒғҚқҢңӨөҰұҮүҺһІі\s-]+$/.test(value)) {
            error = this.$t("screen2.errors.lettersOnly");
          }
        }
        if (q.type === 7) {
          if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) {
            error = this.$t("screen2.errors.invalidEmail");
          }
        }
        if (q.type === 4) {
          const digits = String(value).replace(/\D/g, "");
          if (digits.length < 11) {
            error = this.$t("screen2.errors.invalidPhone");
          }
        }
      }

      this.$set(this.errors, q.id, error);
    },
    markTouched(question) {
      const id = question?.id || question;
      this.$set(this.touched, id, true);
      this.validateField(question);
    },
    onPhoneFocus(question) {
      const id = question?.id || question;
      if (!this.formData[id]) {
        this.$set(this.formData, id, "+7");
      }
    },
    onPhoneInput(question) {
      const id = question?.id || question;
      const val = this.formData[id] || "";
      if (!val.startsWith("+7")) {
        this.formData[id] = "+7" + val.replace(/\D/g, "").slice(1);
      }
      this.validateField(question);
    },
    getFormFields() {
      if (!this.campaignId) {
        this.isLoadingForm = false;
        return;
      }
      this.isLoadingForm = true;
      this.formError = false;
      this.$http
        .get("/api/survey", {
          params: {
            campaignId: this.campaignId
          }
        })
        .then(res => {
          this.formSchema = res.data;
          if (res.data?.questions) {
            res.data.questions.forEach(q => {
              if (q.type === 3) {
                this.$set(this.formData, q.id, this.getUserAge || "");
              } else if (q.type === 6) {
                const defaultVal = (q.answers && q.answers.length > 0) ? q.answers[0].id : "male";
                this.$set(this.formData, q.id, defaultVal);
              } else if (q.type === 11 || q.type === 12) {
                this.$set(this.formData, q.id, false);
              } else {
                this.$set(this.formData, q.id, "");
              }
            });
          }
        })
        .catch(err => {
          this.formError = true;
          console.error("Ошибка загрузки формы:", err);
        })
        .finally(() => {
          this.isLoadingForm = false;
        });
    },
    async sendVerificationCode() {
      let phoneAnswer = "";
      let phoneQuestionId = null;

      if (this.formSchema?.questions) {
        const phoneQuestion = this.formSchema.questions.find(q => q.type === 4);
        if (!phoneQuestion) return false;
        phoneQuestionId = phoneQuestion.id;
        const raw = this.formData[phoneQuestion.id] || "";
        phoneAnswer = raw.replace(/[^\d+]/g, "");
      } else {
        phoneAnswer = (this.form.phone || "").replace(/[^\d+]/g, "");
      }

      if (!phoneAnswer.startsWith("+")) {
        phoneAnswer = "+" + phoneAnswer;
      }

      try {
        this.isSendSms = true;
        let recaptchaToken = "";
        try {
          if (this.$recaptchaLoaded && this.$recaptcha) {
            await this.$recaptchaLoaded();
            recaptchaToken = await this.$recaptcha("send_sms");
          }
        } catch (reErr) {
          console.warn("Recaptcha bypass/error:", reErr);
        }

        const answerDto = {
          campaignId: this.campaignId,
          surveyAnswer: {
            surveyQuestionId: phoneQuestionId,
            answer: phoneAnswer
          },
          recaptchaToken: recaptchaToken
        };

        await this.$http.post("/api/Verification/SendCode", answerDto);
        console.log("Код успешно отправлен!");
        this.otpErrorSms = false;
        return true;
      } catch (err) {
        console.error("Ошибка при отправке кода:", err);
        if (err?.response?.status === 409) {
          this.otpErrorSms = true;
          this.showOtpPopup = true;
          this.otpSuccess = false;
          return false;
        }
        if (err?.response?.status === 403) {
          this.snackbar = true;
          this.message = this.$t("screen2.errors.botDetected");
          return false;
        }
        this.snackbar = true;
        this.message = err?.response?.data?.message || this.$t("screen2.errors.sendCodeError");
        return false;
      } finally {
        this.isSendSms = false;
      }
    },
    async resendSms() {
      await this.startSmsTimer();
      await this.sendVerificationCode();
    },
    async startSmsTimer() {
      this.smsTimer = 60;
      if (this.smsTimerInterval) clearInterval(this.smsTimerInterval);
      this.smsTimerInterval = setInterval(() => {
        if (this.smsTimer > 0) {
          this.smsTimer--;
        } else {
          clearInterval(this.smsTimerInterval);
          this.smsTimerInterval = null;
        }
      }, 1000);
    },
    async confirmRegistration() {
      this.otpError = "";

      if (this.formSchema?.questions && this.formSchema.questions.length > 0) {
        const hasEmptyField = Object.keys(this.formData).some(key => {
          const value = this.formData[key];
          const q = this.formSchema.questions.find(item => String(item.id) === String(key));
          if (!q) return false;

          if (q.type === 11 || q.type === 12) {
            return value !== true;
          }
          return value === null || value === undefined || value === "";
        });

        const hasErrors = Object.values(this.errors).some(error => error && error.length > 0);
        if (hasEmptyField || hasErrors) {
          this.snackbar = true;
          this.message = hasErrors
            ? this.$t("screen2.errors.fixErrors")
            : this.$t("screen2.errors.fillAllFields");
          return;
        }
      } else {
        if (!this.form.fullName || !this.form.fullName.trim()) {
          this.snackbar = true;
          this.message = this.$t("screen2.errors.enterFullName");
          return;
        }
        if (!this.form.phone || this.form.phone.replace(/\D/g, "").length < 10) {
          this.snackbar = true;
          this.message = this.$t("screen2.errors.invalidPhone");
          return;
        }
        if (!this.form.consentData || !this.form.consentMarketing) {
          this.snackbar = true;
          this.message = this.$t("screen2.errors.consentNeeded");
          return;
        }
      }

      try {
        this.isSendSms = true;
        const isSent = await this.sendVerificationCode();
        if (isSent && !this.otpErrorSms) {
          this.otpError = "";
          this.otpSuccess = false;
          this.showOtpPopup = true;
          this.startSmsTimer();
        }
      } catch (e) {
        console.error("Ошибка подтверждения регистрации:", e);
        this.snackbar = true;
        this.message = this.$t("screen2.errors.sendCodeError");
      } finally {
        this.isSendSms = false;
      }
    },
    async submitAnswersWithCode(code) {
      this.otpVerifying = true;
      this.otpError = "";
      this.otpSuccess = false;

      try {
        let payload = null;

        if (this.formSchema?.questions) {
          payload = {
            surveyId: this.formSchema.id,
            campaignId: this.campaignId,
            verificationCode: code,
            answers: Object.entries(this.formData).map(([key, value]) => {
              let answer = String(value);
              if (/^\+7[\d\s()-]{10,}$/.test(answer)) {
                answer = answer.replace(/[^\d+]/g, "");
              }
              return {
                surveyQuestionId: key,
                answer
              };
            })
          };
        } else {
          payload = {
            campaignId: this.campaignId,
            verificationCode: code,
            answers: [
              { name: "fullName", answer: this.form.fullName },
              { name: "gender", answer: this.form.gender },
              { name: "phone", answer: this.form.phone.replace(/[^\d+]/g, "") }
            ]
          };
        }

        const res = await this.$http.post("/api/AnswerSet", payload);
        if (res && res.data) {
          this.answerSetId =
            res.data.id ||
            res.data.answerSetId ||
            (typeof res.data === "string" ? res.data : null);
        }
        this.otpSuccess = true;

        // После успешной верификации плавно переходим к шагу 3 (Колесо Фортуны)
        setTimeout(() => {
          this.showOtpPopup = false;
          this.goToStep(3);
        }, 1200);
      } catch (error) {
        console.error("Ошибка подтверждения кода:", error);
        const msg = error?.response?.data?.message || this.$t("screen2.errors.invalidCode");
        this.otpError = msg;
      } finally {
        this.otpVerifying = false;
      }
    },
    async handleSpin() {
      if (this.isSpinning) return;

      this.$emit("request-spin", {
        formData: this.formSchema ? this.formData : this.form
      });

      this.isSpinning = true;

      try {
        let spinResult = null;

        // Если заданы wheelId и answerSetId, отправляем запрос к бэкенду
        if (this.wheelId && this.answerSetId) {
          const spinPayload = {
            wheelId: this.wheelId,
            answerSetId: this.answerSetId
          };
          const res = await this.$http.post("/api/wheel/SpinWheel", spinPayload);
          spinResult = res.data;
        } else {
          // Демо-фоллбек для локального тестирования
          console.warn(
            "wheelId или answerSetId отсутствует, используется демонстрационный ответ из справочника"
          );
          const samplePhysicalGifts = [
            null, // Промокод (только дефолтный подарок)
            {
              id: "64a02526-2996-f111-b801-00155d79212a",
              gift: {
                id: "64a02526-2996-f111-b801-00155d79212a",
                name: "Вентилятор",
                name_Kaz: "Желдеткіш",
                description: "Портативный вентилятор для прохлады в жаркие дни",
                description_Kaz: "Ыстық күндерде салқындауға арналған ықшам желдеткіш"
              }
            },
            {
              id: "ce380775-299a-f111-b802-00155d79212a",
              gift: {
                id: "ce380775-299a-f111-b802-00155d79212a",
                name: "Козырёк",
                name_Kaz: "Күнкепка",
                description: "Стильный фестивальный козырёк",
                description_Kaz: "Фестивальге арналған стильді күнкепка"
              }
            },
            {
              id: "544e0da8-2996-f111-b801-00155d79212a",
              gift: {
                id: "544e0da8-2996-f111-b801-00155d79212a",
                name: "Плед",
                name_Kaz: "Плед",
                description: "Уютный брендированный плед",
                description_Kaz: "Жайлы фирмалық плед"
              }
            },
            {
              id: "c25f6d47-299a-f111-b802-00155d79212a",
              gift: {
                id: "c25f6d47-299a-f111-b802-00155d79212a",
                name: "Сумка",
                name_Kaz: "Сөмке",
                description: "Стильная и удобная фирменная сумка",
                description_Kaz: "Ыңғайлы әрі стильді сөмке"
              }
            }
          ];

          const randomPhysical = samplePhysicalGifts[Math.floor(Math.random() * samplePhysicalGifts.length)];

          spinResult = {
            defaultGift: {
              id: "df0c4c3b-4f96-f111-b801-00155d79212a",
              gift: {
                id: "d0410ec2-2896-f111-b801-00155d79212a",
                name: "Промокод",
                name_Kaz: "Промокод",
                description: "Супер промокод на 20%",
                description_Kaz: "20% супер промокод"
              },
              promoCode: {
                id: "33973647-2696-f111-b801-00155d79212a",
                code: "qwertyuio"
              }
            },
            gift: randomPhysical
          };
        }

        this.applySpinResult(spinResult);
      } catch (error) {
        console.error("Ошибка при запросе /api/wheel/SpinWheel:", error);
        this.isSpinning = false;
        const msg =
          error?.response?.data?.message ||
          "Ошибка при получении выигрыша. Попробуйте еще раз.";
        this.snackbar = true;
        this.message = msg;
      }
    },
    applySpinResult(spinResult, doSpin = true) {
      this.lastSpinResult = spinResult;
      const defaultGift = spinResult?.defaultGift;
      const physicalGift = spinResult?.gift;
      const hasPhysicalGift = Boolean(
        physicalGift && (physicalGift.gift || physicalGift.name || physicalGift.id)
      );

      // Извлекаем промокод (присутствует всегда)
      const promoCode =
        defaultGift?.promoCode?.code ||
        physicalGift?.promoCode?.code ||
        (typeof defaultGift?.promoCode === "string" ? defaultGift.promoCode : "") ||
        "";

      let winningIndex = 0;
      const isKz = this.currentLang === "kz";

      if (hasPhysicalGift) {
        // 1. Выпал физический приз из справочника
        const giftObj = physicalGift.gift || physicalGift;
        const giftId = String(giftObj.id || physicalGift.id || physicalGift.giftId || "").toLowerCase();
        const catalogItem = this.giftsCatalog[giftId];

        let title = "";
        let subtitle = "";
        let icon = "🎁";

        if (catalogItem) {
          winningIndex = catalogItem.sectorIndex;
          icon = catalogItem.icon || "🎁";
          const rawTitle = (isKz && giftObj.name_Kaz) || giftObj.name || catalogItem.title;
          title = rawTitle.toUpperCase();
          subtitle =
            (isKz && giftObj.description_Kaz) ||
            giftObj.description ||
            catalogItem.subtitle ||
            this.$t("screen4.physicalInstruction");
        } else {
          // Запасной поиск по наименованию или секторам
          const giftName = ((isKz && giftObj.name_Kaz) || giftObj.name || "").toLowerCase();
          const matchedSector = this.sectors.find(
            s =>
              s.type === "icon" &&
              (giftName.includes(s.text.toLowerCase()) ||
                (s.giftId && s.giftId.toLowerCase() === giftId))
          );

          if (matchedSector) {
            winningIndex = matchedSector.id;
            icon = matchedSector.icon || "🎁";
          } else {
            const iconSectors = [1, 3, 5, 7];
            winningIndex = iconSectors[Math.floor(Math.random() * iconSectors.length)];
            icon = this.sectors[winningIndex]?.icon || "🎁";
          }

          title = ((isKz && giftObj.name_Kaz) || giftObj.name || this.$t("prizes.defaultPhysicalTitle")).toUpperCase();
          subtitle =
            (isKz && giftObj.description_Kaz) ||
            giftObj.description ||
            this.$t("screen4.physicalInstruction");
        }

        this.prize = {
          id: winningIndex,
          type: "physical",
          isPhysical: true,
          title: title,
          subtitle: subtitle,
          icon: icon,
          promoCode: promoCode,
          expiryDate: isKz ? "30 қыркүйекке" : "30 сентября",
          partnerBaseUrl: "https://newxo.kz/product/vodka-absolut-tabasco-07-l"
        };
      } else {
        // 2. Выпал только промокод
        const giftObj = defaultGift?.gift || defaultGift;
        const giftId = String(giftObj?.id || "").toLowerCase();
        const catalogItem =
          this.giftsCatalog[giftId] ||
          this.giftsCatalog["d0410ec2-2896-f111-b801-00155d79212a"];

        const rawName = (isKz && giftObj?.name_Kaz) || giftObj?.name;
        const title = rawName
          ? rawName.toUpperCase() === "ПРОМОКОД"
            ? this.$t("prizes.promocode.title")
            : rawName.toUpperCase()
          : catalogItem?.title || this.$t("prizes.promocode.title");
        const subtitle =
          (isKz && giftObj?.description_Kaz) ||
          giftObj?.description ||
          catalogItem?.subtitle ||
          this.$t("prizes.promocode.subtitle");

        if (this.lastWinningIndex !== null && [0, 2, 4, 6].includes(this.lastWinningIndex)) {
          winningIndex = this.lastWinningIndex;
        } else {
          const promoSectors = [0, 2, 4, 6];
          winningIndex = promoSectors[Math.floor(Math.random() * promoSectors.length)];
        }

        this.prize = {
          id: winningIndex,
          type: "digital",
          isPhysical: false,
          title: title,
          subtitle: subtitle,
          icon: null,
          promoCode: promoCode,
          expiryDate: isKz ? "30 қыркүйекке" : "30 сентября",
          partnerBaseUrl: "https://newxo.kz/product/vodka-absolut-tabasco-07-l"
        };
      }

      this.lastWinningIndex = winningIndex;

      if (doSpin) {
        this.spinWheel(winningIndex);
      }
    },
    spinWheel(winningIndex) {
      this.isSpinning = true;

      const fullSpins = 5;
      const totalSectors = this.sectors.length;
      const sectorAngle = 360 / totalSectors;

      const targetSectorAngle = (360 - winningIndex * sectorAngle) % 360;

      const currentRot = this.wheelRotation;
      const currentBase = Math.ceil(currentRot / 360) * 360;
      const targetRotation = currentBase + fullSpins * 360 + targetSectorAngle;

      this.wheelRotation = targetRotation;
      this.winningSector = this.sectors[winningIndex];

      setTimeout(() => {
        this.isSpinning = false;
        this.$emit("spin-complete", {
          winningSector: this.winningSector,
          winningIndex: winningIndex,
          prize: this.prize
        });
        this.nextStep();
      }, 4100);
    },
    copyPromoCode() {
      if (!this.prize || !this.prize.promoCode) return;
      const textToCopy = this.prize.promoCode;

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(textToCopy)
          .then(() => {
            this.showCopiedState();
          })
          .catch(() => {
            this.fallbackCopy(textToCopy);
          });
      } else {
        this.fallbackCopy(textToCopy);
      }
    },
    fallbackCopy(text) {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        this.showCopiedState();
      } catch (err) {
        console.error("Не удалось скопировать промокод", err);
      }
      document.body.removeChild(textArea);
    },
    showCopiedState() {
      this.copied = true;
      setTimeout(() => {
        this.copied = false;
      }, 2000);
    }
  }
};
</script>

<style scoped>
/* Глобальная обертка */
.fortune-spin-wrapper {
  width: 100%;
  min-height: 100vh;
  background-color: var(--color-primary, #DD1814);
  font-family: var(--font-family, "MontserratRegular", sans-serif);
  color: var(--color-white, #FFFFFF);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  box-sizing: border-box;
}

/* --- Десктопная заглушка --- */
.desktop-stub {
  display: none;
  width: 100%;
  min-height: 100vh;
  background: linear-gradient(135deg, #1a0303 0%, #DD1814 100%);
  justify-content: center;
  align-items: center;
  padding: 40px 20px;
  text-align: center;
  box-sizing: border-box;
}

.desktop-stub-content {
  max-width: 480px;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: var(--radius-lg, 20px);
  padding: 48px 32px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
}

.desktop-stub-icon {
  font-size: 64px;
  margin-bottom: 20px;
}

.desktop-stub-title {
  font-family: var(--font-family-black, "MontserratBlack", sans-serif);
  font-size: var(--font-size-lg, 24px);
  font-weight: 900;
  text-transform: uppercase;
  color: var(--color-white, #FFFFFF);
  margin-bottom: 16px;
  line-height: 1.3;
}

.desktop-stub-subtitle {
  font-family: var(--font-family, "MontserratRegular", sans-serif);
  font-size: var(--font-size-xs, 16px);
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.6;
  margin: 0;
}

/* Media query для десктопа (ширина > 768px) */
@media screen and (min-width: 900px) {
  .desktop-stub {
    display: flex;
  }
  .mobile-container {
    display: none !important;
  }
}

/* --- Мобильный контейнер --- */
.mobile-container {
  width: 100%;
  /* max-width: 80px; */
  min-height: 100vh;
  background-color: var(--color-primary, #DD1814);
  display: flex;
  flex-direction: column;
  position: relative;
  box-sizing: border-box;
  margin: 0 auto;
}

/* Степпер индикатор */
.stepper-bar {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: var(--color-primary-dark, #A90C01);
  z-index: 10;
}

.stepper-dot {
  width: 24px;
  height: 4px;
  border-radius: 2px;
  background-color: rgba(255, 255, 255, 0.3);
  cursor: default;
  pointer-events: none;
  transition: all var(--transition-fast, 0.15s ease-out);
}

.stepper-dot.active {
  background-color: var(--color-white, #FFFFFF);
  width: 36px;
}

.stepper-dot.completed {
  background-color: rgba(255, 255, 255, 0.7);
}

/* Экран */
.screen {
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  background-color: var(--color-primary, #DD1814);
}

/* Header */
.screen-header {
  background-color: var(--color-primary, #DD1814);
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  box-sizing: border-box;
}

.header-left,
.header-right {
  font-family: var(--font-family, "MontserratRegular", sans-serif);
  font-size: var(--font-size-xxxs, 12px);
  font-weight: 400;
  color: var(--color-white, #FFFFFF);
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.header-right-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.header-age-badge {
  font-family: var(--font-family-bold, "MontserratBold", sans-serif);
  font-size: var(--font-size-xxxs, 12px);
  font-weight: 700;
  color: var(--color-white, #FFFFFF);
  letter-spacing: 0.5px;
}

.header-brand-text {
  font-family: var(--font-family-bold, "MontserratBold", sans-serif);
  font-size: var(--font-size-xxxs, 12px);
  font-weight: 700;
  color: var(--color-white, #FFFFFF);
  letter-spacing: 0.5px;
}

/* Стили переключателя языков */
.desktop-stub-top {
  display: flex;
  justify-content: flex-end;
  width: 100%;
  margin-bottom: 16px;
}

.lang-switch-container {
  display: inline-flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 18px;
  padding: 2px 4px;
  gap: 2px;
}

.lang-btn {
  background: transparent;
  border: none;
  outline: none;
  font-family: var(--font-family-bold, "MontserratBold", sans-serif);
  font-size: 11px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.85);
  padding: 3px 8px;
  border-radius: 14px;
  cursor: pointer;
  line-height: 1.1;
  letter-spacing: 0.5px;
  transition: all 0.2s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.lang-btn:hover {
  color: #FFFFFF;
}

.lang-btn.active {
  background: #FFFFFF;
  color: var(--color-primary, #DD1814);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}

.lang-divider {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.4);
  user-select: none;
}

/* --- ЭКРАН 1 --- */
.main-image-wrapper {
  width: 100%;
  line-height: 0;
  overflow: hidden;
}

.main-image {
  width: 100%;
  height: auto;
  display: block;
  object-fit: cover;
}

.bottom-block {
  background-color: var(--color-primary, #DD1814);
  padding: 24px 20px 32px 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  box-sizing: border-box;
}

.sub-header-text {
  font-family: var(--font-family-medium, "MontserratMedium", sans-serif);
  font-size: var(--font-size-xs, 16px);
  font-weight: 600;
  color: var(--color-white, #FFFFFF);
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 8px;
  text-align: left;
  align-self: flex-start;
  width: 100%;
}

.main-title {
  font-family: var(--font-family-black, "MontserratBlack", sans-serif);
  font-size: clamp(56px, 9vw, 56px);
  font-weight: 900;
  line-height: 1.05;
  color: var(--color-white, #FFFFFF);
  margin: 0 0 16px 0;
  text-transform: uppercase;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  align-self: flex-start;
  width: 100%;
}

.title-line {
  display: block;
  letter-spacing: 0.5px;
}

.instruction-text {
  font-family: var(--font-family-bold, "MontserratBold", sans-serif);
  font-size: var(--font-size-xs, 16px);
  font-weight: 600;
  color: var(--color-white, #FFFFFF);
  line-height: 1.35;
  margin: 0 0 24px 0;
  text-transform: uppercase;
  text-align: left;
  align-self: flex-start;
  width: 100%;
}

/* Кнопки */
.btn-participate {
  width: 100%;
  max-width: 340px;
  background-color: var(--color-white, #FFFFFF);
  color: var(--color-primary-dark, #A90C01);
  font-family: var(--font-family-black, "MontserratBlack", sans-serif);
  font-size: var(--font-size-md, 20px);
  font-weight: 900;
  padding: 16px 24px;
  border-radius: var(--radius-md, 10px);
  border: none;
  cursor: pointer;
  text-transform: uppercase;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.2);
  transition: transform var(--transition-fast, 0.15s ease-out), box-shadow var(--transition-fast, 0.15s ease-out);
  margin-bottom: 16px;
  outline: none;
  align-self: center;
  text-align: center;
}

.btn-participate:active:not(:disabled) {
  transform: translateY(2px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}

.btn-participate:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-retry {
  margin-top: 16px;
}

.disclaimer-text {
  font-family: var(--font-family, "MontserratRegular", sans-serif);
  font-size: var(--font-size-xxxs, 12px);
  font-weight: 400;
  color: var(--color-white, #FFFFFF);
  line-height: 1.4;
  margin: 0;
  text-transform: uppercase;
  max-width: 320px;
  opacity: 0.9;
  align-self: center;
  text-align: center;
}

/* --- ЭКРАН 2 (Форма) --- */
.screen-2 {
  background-color: var(--color-primary, #DD1814);
}

.screen-2-body {
  padding: 20px 20px 32px 20px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.screen-title-block {
  text-align: center;
  margin-bottom: 24px;
}

.subtitle-text {
  font-family: var(--font-family-medium, "MontserratMedium", sans-serif);
  font-size: var(--font-size-xs, 16px);
  font-weight: 500;
  color: var(--color-white, #FFFFFF);
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 4px;
}

.heading-title {
  font-family: var(--font-family-black, "MontserratBlack", sans-serif);
  font-size: clamp(26px, 7vw, 32px);
  font-weight: 900;
  color: var(--color-white, #FFFFFF);
  text-transform: uppercase;
  margin: 0;
  letter-spacing: 0.5px;
}

.form-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 48px 0;
}

.form-error {
  text-align: center;
  padding: 32px 16px;
}

.form-error-text {
  font-family: var(--font-family-bold, "MontserratBold", sans-serif);
  font-size: 16px;
  color: #FFFFFF;
  margin-bottom: 12px;
}

/* Форма */
.registration-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
}

.form-label {
  font-family: var(--font-family-bold, "MontserratBold", sans-serif);
  font-size: var(--font-size-xxs, 14px);
  font-weight: 700;
  color: var(--color-white, #FFFFFF);
}

.form-input {
  width: 100%;
  background: transparent;
  border: 1px solid var(--color-white, #FFFFFF);
  border-radius: var(--radius-md, 10px);
  padding: 14px 16px;
  font-family: var(--font-family, "MontserratRegular", sans-serif);
  font-size: var(--font-size-xs, 16px);
  color: var(--color-white, #FFFFFF);
  outline: none;
  box-sizing: border-box;
  transition: border-color var(--transition-fast, 0.15s ease-out), box-shadow var(--transition-fast, 0.15s ease-out);
}

.form-input::placeholder {
  color: var(--color-white, #FFFFFF);
  opacity: 0.8;
}

.form-input:focus {
  border-color: var(--color-white, #FFFFFF);
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.3);
}

/* Поле телефона */
.phone-input-wrapper {
  display: flex;
  align-items: center;
  width: 100%;
  background: transparent;
  border: 1px solid var(--color-white, #FFFFFF);
  border-radius: var(--radius-md, 10px);
  padding: 0 16px;
  box-sizing: border-box;
  transition: border-color var(--transition-fast, 0.15s ease-out), box-shadow var(--transition-fast, 0.15s ease-out);
}

.phone-input-wrapper:focus-within {
  border-color: var(--color-white, #FFFFFF);
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.3);
}

.phone-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  padding: 14px 0;
  font-family: var(--font-family, "MontserratRegular", sans-serif);
  font-size: var(--font-size-xs, 16px);
  color: var(--color-white, #FFFFFF);
}

.phone-input::placeholder {
  color: var(--color-white, #FFFFFF);
  opacity: 0.8;
}

/* Переключатель пола */
.gender-toggle-group {
  display: flex;
  gap: 12px;
  width: 100%;
}

.gender-btn {
  flex: 1;
  padding: 14px;
  border-radius: var(--radius-md, 10px);
  font-family: var(--font-family-bold, "MontserratBold", sans-serif);
  font-size: var(--font-size-xs, 16px);
  font-weight: 700;
  cursor: pointer;
  transition: all var(--transition-fast, 0.15s ease-out);
  outline: none;
  text-align: center;
}

.gender-btn-red {
  background-color: var(--color-primary, #DD1814);
  border: 1px solid var(--color-white, #FFFFFF);
  color: var(--color-white, #FFFFFF);
}

.gender-btn-white {
  background-color: var(--color-white, #FFFFFF);
  border: 1px solid transparent;
  color: var(--color-primary-dark, #A90C01);
}

/* Ошибки валидации */
.error-message {
  color: #FFE6E6;
  font-weight: 500;
  font-size: 12px;
  font-family: var(--font-family, "MontserratRegular", sans-serif);
  min-height: 18px;
  margin-top: 2px;
}

/* Vuetify Dropdown Select */
.fortune-select ::v-deep .v-input__slot {
  border-radius: var(--radius-md, 10px) !important;
  background: transparent !important;
  min-height: 48px !important;
}

.fortune-select ::v-deep .v-select__selections,
.fortune-select ::v-deep .v-select__selections span,
.fortune-select ::v-deep input,
.fortune-select ::v-deep input::placeholder,
.fortune-select ::v-deep .v-select__selection,
.fortune-select ::v-deep .v-select__selection--comma,
.fortune-select ::v-deep .v-select__placeholder {
  color: var(--color-white, #FFFFFF) !important;
  font-size: 16px !important;
  font-family: var(--font-family, "MontserratRegular", sans-serif) !important;
  font-weight: 400 !important;
}

.fortune-select ::v-deep fieldset {
  border: 1px solid var(--color-white, #FFFFFF) !important;
}

.fortune-select ::v-deep .v-icon {
  color: var(--color-white, #FFFFFF) !important;
}

.select-item-text {
  white-space: normal;
  word-break: break-word;
  color: var(--color-white, #FFFFFF);
}

/* Чекбоксы согласий */
.consent-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 4px;
}

.custom-checkbox-wrapper {
  display: flex;
  flex-direction: column;
}

.custom-checkbox-label {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  position: relative;
  user-select: none;
}

.checkbox-input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.custom-checkbox {
  width: 20px;
  height: 20px;
  min-width: 20px;
  border: 1px solid var(--color-white, #FFFFFF);
  border-radius: 4px;
  background-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
  transition: background-color var(--transition-fast, 0.15s ease-out);
}

.checkbox-input:checked + .custom-checkbox {
  background-color: var(--color-white, #FFFFFF);
}

.checkbox-input:checked + .custom-checkbox::after {
  content: "";
  width: 5px;
  height: 10px;
  border: solid var(--color-primary-dark, #A90C01);
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
  margin-bottom: 2px;
}

.checkbox-text {
  font-family: var(--font-family, "MontserratRegular", sans-serif);
  font-size: var(--font-size-xxxs, 12px);
  color: var(--color-white, #FFFFFF);
  line-height: 1.4;
  text-align: left;
}

.checkbox-text ::v-deep .legal-link,
.checkbox-text .legal-link,
.legal-link {
  color: var(--color-white, #FFFFFF);
  text-decoration: underline;
  cursor: pointer;
  font-weight: 600;
}

.form-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 16px;
  margin-top: 8px;
}

.form-disclaimer {
  font-family: var(--font-family, "MontserratRegular", sans-serif);
  font-size: var(--font-size-xxxs, 12px);
  color: var(--color-white, #FFFFFF);
  margin: 0;
  opacity: 0.95;
}

.btn-submit-step2 {
  max-width: 100%;
  margin-bottom: 0;
}

/* --- ЭКРАН 3 (Колесо Фортуны) --- */
.screen-3 {
  background-color: var(--color-primary, #DD1814);
}

.screen-3-body {
  padding: 16px 20px 32px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  box-sizing: border-box;
  flex: 1;
}

.wheel-main-title {
  font-family: var(--font-family-black, "MontserratBlack", sans-serif);
  font-size: clamp(28px, 8vw, 36px);
  font-weight: 900;
  color: var(--color-white, #FFFFFF);
  text-transform: uppercase;
  margin: 0 0 16px 0;
  letter-spacing: 0.5px;
}

.wheel-container {
  position: relative;
  width: 540px;
  height: 540px;
  max-width: 85vw;
  max-height: 85vw;
  margin: 12px auto 24px auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.wheel-pointer {
  position: absolute;
  top: -16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 20;
  filter: drop-shadow(0px 4px 6px rgba(0, 0, 0, 0.3));
}

.wheel-rotator {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  transition: transform 4s cubic-bezier(0.15, 0.9, 0.2, 1);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
}

.wheel-svg {
  width: 100%;
  height: 100%;
  display: block;
}

.sector-text {
  font-family: var(--font-family-black, "MontserratBlack", sans-serif);
  font-size: 24px;
  font-weight: 900;
  letter-spacing: -0.5px;
}

.sector-emoji {
  font-size: 22px;
}

.sector-label {
  font-family: var(--font-family-black, "MontserratBlack", sans-serif);
  font-size: 8.5px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  user-select: none;
}

.wheel-center-badge {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 72px;
  height: 72px;
  background-color: var(--color-white, #FFFFFF);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  border: 3px solid var(--color-primary-dark, #A90C01);
}

.badge-text {
  font-family: var(--font-family-black, "MontserratBlack", sans-serif);
  font-weight: 900;
  font-size: 18px;
  color: var(--color-primary-dark, #A90C01);
  letter-spacing: 0.5px;
}

.wheel-disclaimer {
  font-family: var(--font-family-bold, "MontserratBold", sans-serif);
  font-size: var(--font-size-xs, 15px);
  font-weight: 700;
  color: var(--color-white, #FFFFFF);
  text-transform: uppercase;
  margin: 0 0 20px 0;
  letter-spacing: 0.5px;
}

.btn-spin {
  margin-top: auto;
  margin-bottom: 0;
}

/* --- ЭКРАН 4 (Экран результата) --- */
.screen-4 {
  background-color: var(--color-primary, #DD1814);
}

.screen-4-body {
  padding: 24px 20px 32px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  box-sizing: border-box;
  flex: 1;
}

.result-title-block {
  margin-bottom: 24px;
}

.prize-title {
  font-family: var(--font-family-black, "MontserratBlack", sans-serif);
  font-size: clamp(26px, 7.5vw, 34px);
  font-weight: 900;
  color: var(--color-white, #FFFFFF);
  text-transform: uppercase;
  line-height: 1.15;
  margin: 0 0 8px 0;
  letter-spacing: 0.5px;
}

.prize-subtitle {
  font-family: var(--font-family, "MontserratRegular", sans-serif);
  font-size: var(--font-size-xs, 16px);
  color: var(--color-white, #FFFFFF);
  margin: 0;
  line-height: 1.4;
  opacity: 0.95;
}

/* Инструкция для физического приза */
.physical-instruction-card {
  background: transparent;
  border: 1px solid var(--color-white, #FFFFFF);
  border-radius: var(--radius-md, 10px);
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  margin-bottom: 24px;
  box-sizing: border-box;
  text-align: left;
}

.instruction-icon {
  font-size: 28px;
  line-height: 1;
  min-width: 32px;
  text-align: center;
}

.instruction-text-right {
  font-family: var(--font-family, "MontserratRegular", sans-serif);
  font-size: var(--font-size-xxs, 14px);
  color: var(--color-white, #FFFFFF);
  line-height: 1.4;
  margin: 0;
}

/* 3. Блок карточки с промокодом */
.promo-card-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
  box-sizing: border-box;
}

.promo-card {
  background-color: var(--color-white, #FFFFFF);
  border-radius: var(--radius-lg, 20px);
  padding: 20px 24px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  box-sizing: border-box;
}

.promo-header-text {
  font-family: var(--font-family, "MontserratRegular", sans-serif);
  font-size: var(--font-size-xxs, 13px);
  color: var(--color-primary, #DD1814);
  margin: 0 0 4px 0;
  line-height: 1.2;
}

.promo-code-value {
  font-family: var(--font-family-black, "MontserratBlack", sans-serif);
  font-size: clamp(28px, 8vw, 36px);
  font-weight: 900;
  color: var(--color-primary, #DD1814);
  text-transform: uppercase;
  letter-spacing: 2px;
  margin: 4px 0 8px 0;
  line-height: 1.1;
}

.promo-instruction-text {
  font-family: var(--font-family, "MontserratRegular", sans-serif);
  font-size: var(--font-size-xxs, 13px);
  color: var(--color-primary, #DD1814);
  margin: 0;
  line-height: 1.2;
}

.promo-expiry-text {
  font-family: var(--font-family, "MontserratRegular", sans-serif);
  font-size: var(--font-size-xxs, 14px);
  color: var(--color-white, #FFFFFF);
  text-align: center;
  line-height: 1.4;
  margin: 12px 0 0 0;
  opacity: 0.95;
}

/* 4. Блок с QR-кодом */
.qr-block-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 24px;
  box-sizing: border-box;
}

.qr-card {
  width: 240px;
  height: 240px;
  background-color: var(--color-white, #FFFFFF);
  border-radius: var(--radius-lg, 20px);
  padding: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  box-sizing: border-box;
}

.qr-code-svg {
  width: 100%;
  height: 100%;
  display: block;
}

.qr-caption-text {
  font-family: var(--font-family, "MontserratRegular", sans-serif);
  font-size: var(--font-size-xxs, 14px);
  color: var(--color-white, #FFFFFF);
  text-align: center;
  margin: 12px 0 0 0;
  opacity: 0.95;
  line-height: 1.3;
}

.btn-partner-link {
  display: inline-block;
  text-decoration: none;
  text-align: center;
  line-height: 1.2;
  width: 100%;
  margin-top: auto;
  margin-bottom: 0;
  box-sizing: border-box;
}

/* Кнопка "Следующий участник" на экране 4 */
.btn-next-participant {
  background: var(--color-white, #FFFFFF);
  color: var(--color-primary, #DD1814);
  margin-top: 14px;
  margin-bottom: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 2px solid #FFFFFF;
  font-family: var(--font-family-bold, "MontserratBold", sans-serif);
  font-size: var(--font-size-md, 18px);
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.btn-next-participant:hover {
  background: #f5f5f5;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25);
}

.btn-next-participant:active {
  transform: translateY(1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}

/* Кнопка быстрого сброса / нового участника в шапке */
.btn-promoter-reset {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 18px;
  padding: 3px 8px;
  font-family: var(--font-family-bold, "MontserratBold", sans-serif);
  font-size: 11px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  line-height: 1.1;
  letter-spacing: 0.3px;
  transition: all 0.2s cubic-bezier(0.25, 0.8, 0.25, 1);
  outline: none;
}

.btn-promoter-reset:hover {
  background: rgba(255, 255, 255, 0.25);
  color: #FFFFFF;
  border-color: rgba(255, 255, 255, 0.7);
}

.btn-promoter-reset:active {
  transform: scale(0.96);
}

.promoter-reset-icon {
  font-size: 12px;
  font-weight: bold;
}

.promoter-reset-text {
  display: none;
}

@media (min-width: 400px) {
  .promoter-reset-text {
    display: inline;
  }
}

/* --- Стили для попапа с юридической информацией --- */
.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  padding: 20px;
  box-sizing: border-box;
}

.popup-content {
  background: var(--color-primary-dark, #A90C01);
  padding: 24px;
  border-radius: var(--radius-lg, 20px);
  width: 100%;
  max-width: 540px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-sizing: border-box;
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.header-text {
  font-family: var(--font-family-bold, "MontserratBold", sans-serif);
  font-weight: 700;
  font-size: 16px;
  text-transform: uppercase;
  color: #FFFFFF;
}

.popup-close-1 {
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 4px;
}

.popup-text-1 {
  font-family: var(--font-family, "MontserratRegular", sans-serif);
  font-weight: 400;
  font-size: 14px;
  color: #FFFFFF;
  line-height: 1.6;
  white-space: pre-line;
  padding-right: 8px;
  overflow-y: auto;
  flex: 1;
}

.popup-text-1::-webkit-scrollbar {
  width: 6px;
}

.popup-text-1::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.6);
  border-radius: 10px;
}

.popup-text-1::-webkit-scrollbar-track {
  background: transparent;
}

/* --- Стили для снэкбара --- */
.fortune-snackbar ::v-deep .v-snack__wrapper {
  background-color: #222222 !important;
  color: #FFFFFF !important;
  border-radius: 10px !important;
}

.snackbar-btn {
  color: #DD1814 !important;
  font-weight: bold;
  cursor: pointer;
  padding: 0 8px;
}
</style>
